import React, { FunctionComponent, useMemo } from 'react';
import { ClientPet } from '@spike/client-model';
import { faDog } from '@fortawesome/pro-regular-svg-icons';
import { SelectDropdown, SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { RewardsActivity, RewardsPagination } from '@spike/loyalty-model';
import ActivityRow from './ActivityRow';
import RewardsEmptyState from './RewardsEmptyState';
import styles from './RecentActivity.module.scss';

export type PetFilterValue = number | 'all';

export interface RecentActivityProps {
    activities: Array<RewardsActivity>;
    pets: Array<ClientPet>;
    selectedPetId: PetFilterValue;
    onChangePetId: (value: PetFilterValue) => void;
    pagination?: RewardsPagination;
    isPageLoading?: boolean;
    onPageChange?: (page: number) => void;
}

const ALL_PETS_OPTION: SelectDropdownOption<PetFilterValue> = { id: 'all', label: 'All Pets' };

const RecentActivity: FunctionComponent<RecentActivityProps> = ({
    activities,
    pets,
    selectedPetId,
    onChangePetId,
    pagination,
    isPageLoading,
    onPageChange
}) => {
    const petOptions = useMemo<Array<SelectDropdownOption<PetFilterValue>>>(
        () => [ALL_PETS_OPTION, ...pets.map(pet => ({ id: pet.id, label: pet.name }))],
        [pets]
    );

    const selectedOption = petOptions.find(option => option.id === selectedPetId) ?? ALL_PETS_OPTION;

    const sortedActivities = useMemo(
        () => [...activities].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        [activities]
    );

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <h2 className={styles.title}>Recent Activity</h2>

                <div className={styles.filter}>
                    <SelectDropdown<PetFilterValue>
                        options={petOptions}
                        value={selectedOption}
                        onChange={option => onChangePetId(option?.id ?? 'all')}
                        buttonProps={{
                            'variant': 'outlined',
                            'leftIcon': faDog,
                            'aria-label': 'Pet filter'
                        }}
                        dropdownProps={{
                            align: 'start',
                            minWidth: 180,
                            maxHeight: 300
                        }}
                    />
                </div>
            </div>

            {sortedActivities.length > 0 ? (
                <div className={styles.list}>
                    {sortedActivities.map(activity => (
                        <ActivityRow
                            key={activity.id}
                            activity={activity}
                        />
                    ))}
                </div>
            ) : (
                <RewardsEmptyState />
            )}

            {pagination && pagination.totalPages > 1 && (
                <Pagination
                    className={styles.pagination}
                    page={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    isLoading={isPageLoading}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
};

export default RecentActivity;
